import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from '../../components/PremiumPageHeader';
import { ListPageFloatingControls } from '../../components/ListPageLayout';
import ListViewSettings from '../../components/ListViewSettings';
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getAllCheckIn, printDcCheckin } from "../../services/CheckInServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildCheckInQueryParams } from "../../utils/listViewDateRanges";
import { displayError } from "../../helpers";
import { mapCheckInListRow } from "./checkInListUtils";

function CheckInTable() {
    const InitialData = {
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const columns = useMemo(
        () => [
            { fieldName: "plant", headerName: 'Plant' },
            { fieldName: "checkout_no", headerName: 'CheckOut No.' },
            { fieldName: "delivery_challan_no", headerName: 'DC Nos' },
            { fieldName: "delivery_challan_date", headerName: 'DC Date' },
            { fieldName: "order_no", headerName: 'SO Nos' },
            { fieldName: 'customer_name', headerName: 'Billing Name' },
            { fieldName: "consignee_name", headerName: 'Site Name' },
            { fieldName: "grade", headerName: 'Grade' },
            { fieldName: "dc_qty", headerName: 'DC Qty' },
            { fieldName: "accepted_qty", headerName: 'Accepted Qty' },
            { fieldName: "customer_status", headerName: 'Customer Status' },
            { fieldName: "cus_dc_status", headerName: 'DC Status' },
            { fieldName: "vehicle_no", headerName: 'Vehicle No' },
            { fieldName: 'driver_name', headerName: 'Drivers Name' },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const loadCheckInList = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllCheckIn(cookies, buildCheckInQueryParams(config))
            .then((checkinList) => {
                if (!checkinList?.checkin_list) {
                    throw new Error('Check-in list was not returned by the server.');
                }
                const tableData = checkinList.checkin_list.map(mapCheckInListRow);
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load check-in list');
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadCheckInList(config);
    }, [loadCheckInList]);

    useEffect(() => {
        getListViewSettings(cookies, 'check_in')
            .then((response) => {
                const config = response.config || {};
                loadCheckInList(config);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load check-in settings');
            });
    }, [cookies, loadCheckInList]);

    const onEditCheckIn = (checkinId) => {
        navigate(`/CheckInEdit/${checkinId}/`);
    };

    const onPrintDeliveryChallan = (checkinId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printDcCheckin(cookies, checkinId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Check In DC',
                    downloadFileName: 'checkin_dc.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Print Failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(c => c.headerName),
            data.records.map((checkin) => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of CheckIn',
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(col => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((checkin) => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
            'CheckInList.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/CheckIn"
                    addNewLabel="New Check In"
                    homeLink="/Home"
                />
                <div className="container">
                    <PremiumPageHeader
                        module="GATE"
                        title="Vehicle Check In"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={(
                            <ListViewSettings
                                viewKey="check_in"
                                onSaved={handleSettingsSaved}
                            />
                        )}
                    />
                </div>
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of CheckIn'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCheckIn}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintDeliveryChallan}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CheckInTable;
